import { BarChart3, Sticker, Phone, Wallet, MonitorSmartphone, ScanEye, GraduationCap, HardDrive, ShieldCheck, MonitorPlay, CheckSquare, BookOpen } from 'lucide-react'
import type { ComponentType } from 'react'
import type { WebModule, AdminSectionProps } from '@os/contracts/web'
import { DelegatedTab } from '../../components/admin/DelegatedTab'

type Icon = ComponentType<{ size?: number }>

// In-core "server" admin provider: the per-server admin sections that are
// self-contained (own their data/fetching) — voice analytics + stickers
// (spaces-owned, in-core for now), telephony + billing (platform), and the
// desktop/monitoring module panels. Chrome + gates mirror the old inline
// renders in ServerAdminPage + shell/adminSections.tsx exactly. Panels are
// lazy so the server-admin bundle is unchanged.
//
// NOTE: do NOT pass this to registry.register() — it is always present via
// admin-registry's STATIC_PROVIDERS.
const serverAdminModule: WebModule = {
  id: 'server',
  version: '0.1.0',
  adminSections: [
    {
      id: 'stats',
      label: 'Voice analytics',
      subtitle: 'Voice activity by member and channel',
      scope: 'server',
      order: 70,
      icon: BarChart3 as Icon,
      component: async () => {
        const { default: ServerStatsTab } = await import('@os/spaces/web/components/server/ServerStatsTab')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <DelegatedTab icon={<BarChart3 size={14} />} title="Voice analytics" subtitle="Voice activity for this server">
              <ServerStatsTab serverId={serverId!} />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'voice-settings',
      label: 'Voice settings',
      subtitle: 'Remote control and other in-call permissions',
      scope: 'server',
      order: 75,
      icon: MonitorPlay as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: RemoteControlPolicyPanel } = await import('../../components/admin/voice/RemoteControlPolicyPanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><RemoteControlPolicyPanel serverId={serverId} /></div>
          ),
        }
      },
    },
    {
      id: 'stickers',
      label: 'Stickers',
      subtitle: 'Stickers visible only to server members',
      scope: 'server',
      order: 80,
      icon: Sticker as Icon,
      component: async () => {
        const { default: StickerPacksAdmin } = await import('../../components/admin/StickerPacksAdmin')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <DelegatedTab icon={<Sticker size={14} />} title="Stickers" subtitle="Sticker packs visible only to this server's members">
              <StickerPacksAdmin serverId={serverId} />
            </DelegatedTab>
          ),
        }
      },
    },
    {
      id: 'telephony',
      label: 'Telephony',
      subtitle: 'Numbers, dial-in, SMS campaigns',
      scope: 'server',
      order: 90,
      icon: Phone as Icon,
      gate: { serverOwner: true },
      component: async () => {
        const { default: ServerTelephonyPanel } = await import('../../components/billing/ServerTelephonyPanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><ServerTelephonyPanel serverId={serverId!} /></div>
          ),
        }
      },
    },
    {
      id: 'billing',
      label: 'Billing',
      subtitle: 'Balance, transactions, organization',
      scope: 'server',
      order: 100,
      icon: Wallet as Icon,
      gate: { serverOwner: true },
      component: async () => {
        const { default: ServerBillingPanel } = await import('../../components/billing/ServerBillingPanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><ServerBillingPanel serverId={serverId!} /></div>
          ),
        }
      },
    },
    {
      id: 'desktops',
      label: 'Desktops',
      subtitle: 'Remote desktops - instances, templates, host, billing',
      scope: 'server',
      order: 110,
      icon: MonitorSmartphone as Icon,
      moduleKey: 'desktop',
      gate: { globalAdmin: true },
      component: async () => {
        const { default: DesktopsAdminPanel } = await import('../../components/admin/desktops/DesktopsAdminPanel')
        return {
          default: () => <div className="min-h-0 flex-1 overflow-y-auto p-6"><DesktopsAdminPanel /></div>,
        }
      },
    },
    {
      id: 'monitoring',
      label: 'Monitoring',
      subtitle: 'Employee activity capture - web, files, apps, screenshots',
      scope: 'server',
      order: 120,
      icon: ScanEye as Icon,
      moduleKey: 'monitoring',
      gate: { serverAdmin: true },
      component: async () => {
        const { default: MonitoringAdminPanel } = await import('../../components/admin/monitoring/MonitoringAdminPanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto p-6"><MonitoringAdminPanel serverId={serverId!} /></div>
          ),
        }
      },
    },
    {
      id: 'module:learning',
      label: 'Learning',
      subtitle: 'Training analytics - onboarding compliance + scores',
      scope: 'server',
      order: 130,
      moduleKey: 'learning',
      icon: GraduationCap as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: AnalyticsPanel } = await import('@os/learning/web/components/AnalyticsPanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><AnalyticsPanel serverId={serverId} /></div>
          ),
        }
      },
    },
    {
      id: 'module:files',
      label: 'Files',
      subtitle: 'Storage usage - objects, size, by scope & department',
      scope: 'server',
      order: 140,
      moduleKey: 'files',
      icon: HardDrive as Icon,
      gate: { serverOwner: true },
      component: async () => {
        const { default: FilesUsagePanel } = await import('../../components/admin/files/FilesUsagePanel')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><FilesUsagePanel serverId={serverId} /></div>
          ),
        }
      },
    },
    {
      id: 'module:vault',
      label: 'Vault',
      subtitle: 'Password policy - length, complexity, rotation',
      scope: 'server',
      order: 150,
      moduleKey: 'vault',
      icon: ShieldCheck as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: VaultPolicyAdmin } = await import('@os/vault/web/components/VaultPolicyAdmin')
        return {
          default: ({ serverId }: AdminSectionProps) => (
            <div className="min-h-0 flex-1 overflow-y-auto"><VaultPolicyAdmin serverId={serverId} /></div>
          ),
        }
      },
    },
    {
      id: 'module:tasks',
      label: 'Tasks',
      subtitle: 'Board stats - tasks by status & overdue',
      scope: 'server',
      order: 160,
      moduleKey: 'tasks',
      icon: CheckSquare as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: TasksAdminPanel } = await import('@os/taskboards/web/components/admin/TasksAdminPanel')
        // TasksAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
        return {
          default: ({ serverId }: AdminSectionProps) => <TasksAdminPanel serverId={serverId!} />,
        }
      },
    },
    {
      id: 'module:knowledge',
      label: 'Knowledge',
      subtitle: 'Knowledge base - collections, articles, visibility',
      scope: 'server',
      order: 170,
      moduleKey: 'knowledge',
      icon: BookOpen as Icon,
      gate: { globalAdmin: true },
      component: async () => {
        const { default: KnowledgeAdminPanel } = await import('@os/knowledgebase/web/components/admin/KnowledgeAdminPanel')
        // KnowledgeAdminPanel self-applies `min-h-0 flex-1 overflow-y-auto` — no wrapper div.
        return {
          default: ({ serverId }: AdminSectionProps) => <KnowledgeAdminPanel serverId={serverId!} />,
        }
      },
    },
  ],
}

export default serverAdminModule
